/**
 * HouseLoader — editorial loading state used while we fetch the property details
 * right after the guest taps "Reserve". A hand-drawn stroke of a house fills in
 * via SVG stroke-dashoffset animation; a gold pulse dot hovers under the roofline.
 */
export default function HouseLoader({ label = "Loading your next Experiential Stay" }) {
  return (
    <div data-testid="house-loader" className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-6 py-12 text-center">
      <svg
        viewBox="0 0 120 120"
        width="180"
        height="180"
        className="text-forest"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <style>{`
          @keyframes vygHouseDraw {
            0% { stroke-dashoffset: 800; }
            60% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes vygHouseFade {
            0%, 55% { opacity: 0; transform: scale(0.9); }
            75% { opacity: 1; transform: scale(1); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes vygHouseGlow {
            0%, 100% { opacity: 0.2; }
            50% { opacity: 1; }
          }
          .vyg-house-stroke {
            stroke-dasharray: 800;
            stroke-dashoffset: 800;
            animation: vygHouseDraw 3s ease-in-out infinite;
          }
          .vyg-house-window {
            transform-origin: center;
            animation: vygHouseFade 3s ease-in-out infinite;
          }
          .vyg-house-smoke {
            animation: vygHouseGlow 2.2s ease-in-out infinite;
          }
        `}</style>

        {/* Ground line */}
        <path className="vyg-house-stroke" d="M10 100 L110 100" />
        {/* Main house body */}
        <path className="vyg-house-stroke" d="M25 100 L25 55 L60 25 L95 55 L95 100 Z" />
        {/* Door */}
        <path className="vyg-house-stroke" d="M52 100 L52 72 L68 72 L68 100" />
        {/* Chimney */}
        <path className="vyg-house-stroke" d="M80 44 L80 30 L88 30 L88 52" />

        {/* Window (fades in after stroke completes) */}
        <g className="vyg-house-window" fill="#C8A14A" stroke="none">
          <rect x="33" y="64" width="12" height="12" rx="1" opacity="0.85" />
          <rect x="75" y="64" width="12" height="12" rx="1" opacity="0.85" />
        </g>

        {/* Soft smoke puffs from the chimney */}
        <g className="vyg-house-smoke" fill="#C8A14A" stroke="none">
          <circle cx="84" cy="22" r="2.5" opacity="0.8" />
          <circle cx="90" cy="14" r="2" opacity="0.6" />
          <circle cx="82" cy="8" r="1.6" opacity="0.4" />
        </g>
      </svg>

      <p className="mt-6 font-display text-2xl text-forest">{label}</p>
      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-wider text-amber">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
        </span>
        One moment
      </div>
    </div>
  );
}
